
import { FactProcess, ProcessStatus } from '../types';
import { CaseDatabase } from './db';
import { TwilioService } from './twilioService';
import { sendReminderEmail } from './emailService';

/**
 * Service to manage client communications and notifications.
 * Includes automated simulation logic for reminders and session triggers.
 */
export const NotificationService = {
  
  async sendRescheduleNotification(process: FactProcess, newDate: string): Promise<void> {
    const msg = `Lluis Law: Your appointment has been rescheduled to ${new Date(newDate).toLocaleString()}. Reply RS to reschedule again or STOP to cancel.`;
    await TwilioService.sendSMS(process.client.phone, msg);
    this.logActivity(`[Twilio SMS] Notification delivered to ${process.client.phone}`);
    
    const updated = {
      ...process,
      appointment: { ...process.appointment, dateTime: newDate },
      notifications: { ...process.notifications, rescheduleSent: true },
      lastUpdated: new Date().toISOString()
    };
    await CaseDatabase.save(updated);
  },

  async sendPartialTranscript(process: FactProcess): Promise<void> {
    this.logActivity(`[Email Gateway] Sent to ${process.client.email}: "Transcript Record - Case #${process.id.substring(0,8)}. Attached is your progress. We will resume where we left off."`);
    
    const updated = {
      ...process,
      notifications: { ...process.notifications, transcriptEmailSent: true },
      lastUpdated: new Date().toISOString()
    };
    await CaseDatabase.save(updated);
  },

  async triggerReminder(process: FactProcess, type: '24h' | '15m'): Promise<FactProcess> {
    const timeLabel = type === '24h' ? '24 hours' : '15 minutes';
    const msg = `Lluis Law Reminder: Your intake starts in ${timeLabel}. Need more time? Reply RS to reschedule.`;
    
    await TwilioService.sendSMS(process.client.phone, msg);
    this.logActivity(`[Twilio SMS] ${timeLabel} reminder sent to ${process.client.phone}`);

        // Send email reminder
    if (process.client.email) {
      await sendReminderEmail(
        process.client.email,
        process.client.name,
        new Date(process.appointment.dateTime).toLocaleString(),
        timeLabel
      );
      this.logActivity(`[Email] ${timeLabel} reminder sent to ${process.client.email}`);
    }
    
    const updated = {
      ...process,
      notifications: { 
        ...process.notifications, 
        [type === '24h' ? 'reminder24hSent' : 'reminder15mSent']: true 
      }
    };
    await CaseDatabase.save(updated);
    return updated;
  },

  async triggerAutoStart(process: FactProcess): Promise<void> {
    if (process.appointment.method === 'Telephonic') {
      this.logActivity(`[SYSTEM INITIATED] Connecting Victoria AI to client via Twilio outbound call...`);
      await TwilioService.initiateOutboundCall(process.client.phone, process.id);
    } else {
      const msg = `Lluis Law: Your secure intake portal is now active. Click here to begin: https://factgather.com/intake/${process.id}`;
      await TwilioService.sendSMS(process.client.phone, msg);
      this.logActivity(`[SYSTEM INITIATED] Secure questionnaire link delivered via Twilio SMS to ${process.client.phone}.`);
    }
    
    const updated = {
      ...process,
      status: ProcessStatus.IN_PROGRESS,
      lastUpdated: new Date().toISOString()
    };
    await CaseDatabase.save(updated);
  },

  logActivity(msg: string) {
    const logs = JSON.parse(sessionStorage.getItem('lluis_activity_logs') || '[]');
    logs.unshift({ id: crypto.randomUUID(), msg, timestamp: new Date().toISOString() });
    sessionStorage.setItem('lluis_activity_logs', JSON.stringify(logs.slice(0, 50)));
    // Dispatch event for UI update
    window.dispatchEvent(new Event('activity_log_updated'));
  },

  getLogs(): {id: string, msg: string, timestamp: string}[] {
    return JSON.parse(sessionStorage.getItem('lluis_activity_logs') || '[]');
  }
};
